import {
  Users,
  BookOpen,
  GraduationCap,
  MessageSquare,
  School,
  DollarSign,
  Settings,
  Home,
  Shield,
  UserCheck,
  Library,
  BookOpen as LibraryIcon,
  Search,
  MessageCircle,
  Compass,
  FlaskConical,
  ClipboardList,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";

interface SidebarProps {
  onSectionChange: (section: string) => void;
  className?: string;
}

const AdminSidebar = ({ onSectionChange, className }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const adminMenuItems = [
    { title: "Dashboard", icon: Home },
    { title: "Communication", icon: MessageSquare },
    { title: "Academics", icon: BookOpen },
    { title: "Lessons", icon: Library },
    { title: "Library", icon: LibraryIcon },
    { title: "Research", icon: Search },
    { title: "explore", icon: Compass },
    { title: "Students", icon: Users },
    { title: "Teachers and Staff", icon: GraduationCap },
    { title: "Classes", icon: School },
    { title: "Attendance", icon: UserCheck },
    { title: "Finance", icon: DollarSign },
    { title: "Lab Technician", icon: FlaskConical },
    { title: "Duty Roster", icon: ClipboardList },
    { title: "Exam Timetable", icon: CalendarIcon },
    { title: "Settings", icon: Settings },
    { title: "Test Cases", icon: FlaskConical }, // Using FlaskConical as placeholder or Beaker if available
    { title: "Chat with AI", icon: MessageCircle },
  ];

  const pathMap: Record<string, string> = {
    Dashboard: "/admin-dashboard",
    Communication: "/communication",
    Academics: "/dashboard/academics",
    Lessons: "/lessons",
    Library: "/library",
    Research: "/research",
    explore: "/explore",
    Students: "/students",
    "Teachers and Staff": "/dashboard/staff",
    Classes: "/classes",
    Attendance: "/attendance",
    Finance: "/finance-dashboard",
    "Lab Technician": "/lab-management",
    "Duty Roster": "/duty-roster",
    "Exam Timetable": "/exam-timetable",
    Settings: "/dashboard/settings",
    "Test Cases": "/test-cases",
    "Chat with AI": "/chatbot",
  };

  return (
    <Sidebar className={cn("w-48 sm:w-64 border-r border-gray-200 dark:border-[#364153] bg-white dark:bg-[#020817]", className)}>
      <SidebarContent id="content" className="p-1 dark:bg-[#020817]">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 px-1 flex items-center gap-2">
            <Shield className="h-3 w-3" />
            Admin Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0">
              {adminMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    isActive={location.pathname === pathMap[item.title]}
                    className="w-full justify-start cursor-pointer py-1 px-2 rounded-md transition-all duration-200 touch-manipulation h-7
                               text-gray-700 dark:text-gray-300
                               font-sans hover:font-bold
                               hover:text-gray-900 dark:hover:text-white
                               hover:bg-gray-100 dark:hover:bg-gray-800
                               data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900
                               data-[active=true]:text-blue-700 dark:data-[active=true]:text-blue-300"
                    onClick={() => {
                      navigate(pathMap[item.title]);
                      onSectionChange(item.title);
                    }}
                  >
                    <item.icon className="mr-2 h-3.5 w-3.5 flex-shrink-0" />
                    <span className="text-xs truncate">{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

export default AdminSidebar;